import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Check on initial load
    checkMobile();

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const handleNavClick = () => {
    window.scrollTo(0, 0);
    setIsOpen(false);
  };

  return (
    <nav 
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/90 dark:bg-gray-900/90 backdrop-blur-md shadow-md py-2' 
          : 'bg-white dark:bg-gray-900 py-4'
      }`}
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex justify-between items-center">
          {/* Left side - Social icons (mobile only) + Logo */}
          <div className="flex items-center">
            {/* Logo */}
            <Link to="/" className="flex items-center" onClick={handleNavClick}>
              <img 
                src="/uploads/logo_png.png" 
                alt="DG IT Logo" 
                className="h-10 w-auto"
              />
              <div className="ml-2">
                <h1 className="text-xl font-bold text-blue-500">DG IT <span className="text-sm font-medium">Pvt. Ltd.</span></h1>
                <p className="text-xs text-orange-500 font-medium">Get quality service in Your Budget</p>
              </div>
            </Link>

            {/* Social Media Icons - Mobile Only */}
            {isMobile && (
              <div className="flex space-x-2 ml-4">
                <a 
                  href="https://www.facebook.com/dgitnepal" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-1.5 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors"
                  aria-label="Visit DG IT on Facebook"
                >
                  <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.tiktok.com/@dgit01" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-1.5 rounded-full bg-gray-50 hover:bg-gray-100 transition-colors"
                  aria-label="Visit DG IT on TikTok"
                >
                  <svg className="w-4 h-4 text-gray-900" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
                <a 
                  href="https://np.linkedin.com/company/dg-it" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-1.5 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors"
                  aria-label="Visit DG IT on LinkedIn"
                >
                  <svg className="w-4 h-4 text-blue-700" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
            )}
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="nav-link text-gray-700 hover:text-blue-500 font-medium" onClick={handleNavClick}>Home</Link>
            <Link to="/services" className="nav-link text-gray-700 hover:text-blue-500 font-medium" onClick={handleNavClick}>Services</Link>
            <Link to="/about" className="nav-link text-gray-700 hover:text-blue-500 font-medium" onClick={handleNavClick}>About</Link>
            <Link to="/projects" className="nav-link text-gray-700 hover:text-blue-500 font-medium" onClick={handleNavClick}>Projects</Link>
            <Link to="/contact" className="nav-link text-gray-700 hover:text-blue-500 font-medium" onClick={handleNavClick}>Contact</Link>
            <Link 
              to="/quote" 
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 px-5 py-2 text-white rounded-full transition-all duration-300 shadow hover:shadow-lg transform hover:-translate-y-1"
              onClick={handleNavClick}
            >
              Get a Quote
            </Link>
          </div>

          {/* Right side - Social icons (desktop) + Mobile Menu Button */}
          <div className="flex items-center space-x-4">
            {/* Social Media Icons - Desktop Only */}
            {!isMobile && (
              <div className="flex space-x-3">
                <a 
                  href="https://www.facebook.com/dgitnepal" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors"
                  aria-label="Visit DG IT on Facebook"
                >
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.tiktok.com/@dgit01" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2 rounded-full bg-gray-50 hover:bg-gray-100 transition-colors"
                  aria-label="Visit DG IT on TikTok"
                >
                  <svg className="w-5 h-5 text-gray-900" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
                <a 
                  href="https://np.linkedin.com/company/dg-it" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors"
                  aria-label="Visit DG IT on LinkedIn"
                >
                  <svg className="w-5 h-5 text-blue-700" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <button 
                onClick={toggleMenu}
                className="text-gray-700 hover:text-blue-500 focus:outline-none"
              >
                {isOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && isMobile && (
          <div className="md:hidden mt-4 pb-4 animate-fade-in">
            <div className="flex flex-col space-y-3">
              <Link to="/" className="text-gray-700 hover:text-blue-500 py-2 font-medium" onClick={handleNavClick}>Home</Link>
              <Link to="/services" className="text-gray-700 hover:text-blue-500 py-2 font-medium" onClick={handleNavClick}>Services</Link>
              <Link to="/about" className="text-gray-700 hover:text-blue-500 py-2 font-medium" onClick={handleNavClick}>About</Link>
              <Link to="/projects" className="text-gray-700 hover:text-blue-500 py-2 font-medium" onClick={handleNavClick}>Projects</Link>
              <Link to="/contact" className="text-gray-700 hover:text-blue-500 py-2 font-medium" onClick={handleNavClick}>Contact</Link>
              <Link 
                to="/quote" 
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 px-5 py-2 text-white rounded-full w-full text-center transition-all duration-300"
                onClick={handleNavClick}
              >
                Get a Quote
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
